<!-- eslint-disable no-plusplus -->
<!-- eslint-disable max-len -->
<template>
  <div class="goal-details-page">
    <div class="content">
      <!-- Header with Back Button -->
      <div class="page-header">
        <div class="header-left">
          <button class="back-btn" @click="goBack">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div class="header-content">
            <h1 class="cabin-family bold">{{ (goal && goal.title) || 'Goal Details' }}</h1>
            <p class="secondary-text">{{ (goal && goal.description) || 'Loading...' }}</p>
          </div>
        </div>
        <div class="header-actions" v-if="goal">
          <button class="btn secondary-btn" @click="showEditModal = true">
            <i class="fas fa-edit"></i>
            Edit Goal
          </button>
          <button class="btn primary-btn" @click="showContributionModal = true">
            <i class="fas fa-plus"></i>
            Add Contribution
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading goal details...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <div class="error-icon">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Error Loading Goal</h3>
        <p>{{ error }}</p>
        <button class="btn primary-btn" @click="loadGoal">
          <i class="fas fa-refresh"></i>
          Try Again
        </button>
      </div>

      <!-- Goal Content -->
      <div v-else-if="goal" class="goal-content">
        <!-- Goal Progress Section -->
        <div class="progress-section">
          <div class="progress-card">
            <div class="progress-header">
              <h2 class="cabin-family semi-bold">Progress Overview</h2>
              <div class="progress-percentage">
                <span class="percentage-text">{{ progressPercentage }}%</span>
              </div>
            </div>
            <div class="progress-bar-container">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: progressPercentage + '%' }"
                  :class="getProgressColorClass()"
                ></div>
              </div>
            </div>

            <div class="progress-details">
              <div class="detail-item">
                <span class="label">Current Amount:</span>
                <span class="value">{{ formatCurrency(goal.current_amount || 0) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Target Amount:</span>
                <span class="value">{{ formatCurrency(goal.target_amount) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Remaining:</span>
                <span class="value">{{ formatCurrency(remainingAmount) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Goal Info Cards -->
        <div class="info-cards">
          <div class="info-card">
            <div class="card-icon primary">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="card-content">
              <h3 class="cabin-family semi-bold">Target Date</h3>
              <p>{{ formatDate(goal.target_date) }}</p>
            </div>
          </div>

          <div class="info-card">
            <div class="card-icon success">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="card-content">
              <h3 class="cabin-family semi-bold">Monthly Target</h3>
              <p>{{ formatCurrency(monthlyTarget) }}</p>
            </div>
          </div>

          <div class="info-card">
            <div class="card-icon warning">
              <i class="fas fa-clock"></i>
            </div>
            <div class="card-content">
              <h3 class="cabin-family semi-bold">Days Remaining</h3>
              <p>{{ daysRemaining }} days</p>
            </div>
          </div>
        </div>

        <!-- Contributions History -->
        <div class="contributions-section">
          <div class="section-header">
            <h2 class="cabin-family semi-bold">Contribution History</h2>
            <p class="secondary-text">Track your progress over time</p>
          </div>

          <div v-if="contributions && contributions.length > 0" class="contributions-list">
            <div
              v-for="contribution in contributions"
              :key="contribution.id"
              class="contribution-item"
            >
              <div class="contribution-icon">
                <i class="fas fa-plus-circle"></i>
              </div>
              <div class="contribution-details">
                <div class="contribution-amount">
                  {{ formatCurrency(contribution.amount) }}
                </div>
                <div class="contribution-date">
                  {{ formatDate(contribution.created_at) }}
                </div>
                <div v-if="contribution.description" class="contribution-note">
                  {{ contribution.description }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-piggy-bank"></i>
            </div>
            <h3>No Contributions Yet</h3>
            <p>Start contributing to reach your goal!</p>
            <button class="btn primary-btn" @click="showContributionModal = true">
              Make First Contribution
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Contribution Modal -->
    <ContributionModal
      v-if="showContributionModal"
      :goal="goal"
      @close="showContributionModal = false"
      @contribution-added="handleContributionAdded"
    />

    <!-- Edit Goal Modal -->
    <GoalCreationWizard
      v-if="showEditModal"
      :edit-mode="true"
      :goal="goal"
      @close="showEditModal = false"
      @goal-updated="handleGoalUpdated"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ContributionModal from '../components/goals/ContributionModal.vue';
import GoalCreationWizard from '../components/goals/GoalCreationWizard.vue';
import { formatCurrency } from '../utils/formatters';

export default {
  name: 'GoalDetails',
  components: {
    ContributionModal,
    GoalCreationWizard,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      showContributionModal: false,
      showEditModal: false,
    };
  },
  computed: {
    ...mapGetters('goals', ['currentGoal']),
    goal() {
      return this.currentGoal;
    },
    contributions() {
      return (this.currentGoal && this.currentGoal.goal_transactions) || [];
    },
    progressPercentage() {
      if (!this.goal || !this.goal.target_amount) return 0;
      const percentage = ((this.goal.current_amount || 0) / this.goal.target_amount) * 100;
      return Math.min(Math.round(percentage), 100);
    },
    remainingAmount() {
      if (!this.goal) return 0;
      return Math.max(this.goal.target_amount - (this.goal.current_amount || 0), 0);
    },
    monthlyTarget() {
      if (!this.goal || !this.goal.target_date) return 0;
      const now = new Date();
      const target = new Date(this.goal.target_date);
      const monthsRemaining = Math.max(
        (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth()),
        1,
      );
      return this.remainingAmount / monthsRemaining;
    },
    daysRemaining() {
      if (!this.goal) return 0;
      return Math.max(this.goal.days_remaining || 0, 0);
    },
  },
  async created() {
    await this.loadGoal();
  },
  methods: {
    ...mapActions('goals', ['fetchGoalDetails']),
    formatCurrency,
    async loadGoal() {
      try {
        this.loading = true;
        this.error = null;
        await this.fetchGoalDetails(this.id);
      } catch (error) {
        console.error('Error loading goal:', error);
        this.error = error.message || 'Failed to load goal details';
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push('/goals');
    },
    getProgressColorClass() {
      if (this.progressPercentage >= 100) return 'success';
      if (this.progressPercentage >= 75) return 'primary';
      if (this.progressPercentage >= 50) return 'warning';
      return 'danger';
    },
    formatDate(date) {
      if (!date) return 'Not set';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    handleContributionAdded() {
      this.showContributionModal = false;
      this.loadGoal();
      this.$toast.success('Contribution added successfully!');
    },
    handleGoalUpdated() {
      this.showEditModal = false;
      this.loadGoal();
      this.$toast.success('Goal updated successfully!');
    },
  },
};
</script>

<style scoped>
.goal-details-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.content {
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  gap: 20px;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  padding: 12px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.header-content h1 {
  color: white;
  margin: 0 0 5px 0;
  font-size: 2.5rem;
}

.header-content .secondary-text {
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 20px;
  color: #ff6b6b;
}

.goal-content {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.progress-section {
  width: 100%;
}

.progress-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.progress-header h2 {
  color: #333;
  margin: 0;
}

.percentage-text {
  font-size: 2rem;
  font-weight: bold;
  color: #667eea;
}

.progress-bar-container {
  margin-bottom: 25px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 6px;
  transition: width 0.5s ease;
}

.progress-fill.success {
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
}

.progress-fill.primary {
  background: linear-gradient(90deg, #667eea, #764ba2);
}

.progress-fill.warning {
  background: linear-gradient(90deg, #FF9800, #FFC107);
}

.progress-fill.danger {
  background: linear-gradient(90deg, #f44336, #ff6b6b);
}

.progress-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  color: #666;
  font-weight: 500;
}

.detail-item .value {
  color: #333;
  font-weight: bold;
}

.info-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 15px;
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.info-card:hover {
  transform: translateY(-5px);
}

.card-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
}

.card-icon.primary {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.card-icon.success {
  background: linear-gradient(135deg, #4CAF50, #8BC34A);
}

.card-icon.warning {
  background: linear-gradient(135deg, #FF9800, #FFC107);
}

.card-content h3 {
  margin: 0 0 5px 0;
  color: #333;
  font-size: 1.1rem;
}

.card-content p {
  margin: 0;
  color: #666;
  font-size: 1rem;
  font-weight: 500;
}

.contributions-section {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px;
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.section-header {
  margin-bottom: 25px;
}

.section-header h2 {
  color: #333;
  margin: 0 0 5px 0;
}

.section-header .secondary-text {
  color: #666;
  margin: 0;
}

.contributions-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.contribution-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.contribution-icon {
  width: 40px;
  height: 40px;
  background: #667eea;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.contribution-details {
  flex: 1;
}

.contribution-amount {
  font-weight: bold;
  color: #333;
  font-size: 1.1rem;
}

.contribution-date {
  color: #666;
  font-size: 0.9rem;
  margin-top: 2px;
}

.contribution-note {
  color: #555;
  font-size: 0.9rem;
  margin-top: 5px;
  font-style: italic;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 3rem;
  color: #ddd;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #333;
  margin: 0 0 10px 0;
}

.empty-state p {
  color: #666;
  margin: 0 0 20px 0;
}

/* Button Styles */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-size: 0.95rem;
}

.primary-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
}

.secondary-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
}

.secondary-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .goal-details-page {
    padding: 15px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  .header-left {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .header-content h1 {
    font-size: 2rem;
  }

  .header-actions {
    justify-content: flex-start;
  }

  .info-cards {
    grid-template-columns: 1fr;
  }

  .progress-details {
    grid-template-columns: 1fr;
  }

  .detail-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
}
</style>
